import { getCurrentAppName } from '@doltech/core/lib/auth/authManager';
import withHydrationOnDemand from '@doltech/core/lib/hooks/withDolHydrationOnDemand';
import { withStoreRequiredForEditor } from '@doltech/ui/lib/figma/BlogEditor/core/withStoreRequiredForEditor';
import { useDoExerciseProgressContext } from '@doltech/ui/lib/figma/BlogEditor/shared-components/Exercise/DoExerciseProgressContext';
import { Separator } from '@doltech/ui/lib/figma/Layout/Landing/Content/Separator';
import {
  defaultIsEmptyText,
  isNodeStringEmpty,
} from '@doltech/ui/lib/shared/components/IELTSample/Outline/Paragraph/components/EditorPlaceHolder';
import { compose } from '@doltech/utils/lib/compose';
import { SectionDescriptionSectionTypeEnum } from '@doltech/utils/lib/constants';
import { getPageSampleTitleEnumTextByKey } from '@doltech/utils/lib/enum/PageSampleTitleEnum';
import { getTocTitleById } from '@doltech/utils/lib/text';
import cl from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

import { VocabList } from '../../../../view-vocab/components/VocabList';
import { AuthorPreview } from '../../../components/Preview/AuthorPreview';
import { IeltsSampleHeader } from '../../../components/Preview/IeltsSampleHeader';
import { LeftSidebarPreviewSample } from '../../../components/Preview/LeftSidebarPreviewSample';

import dynamic from 'next/dynamic';
import { SamplePromotionSidebar } from '../../../components/Preview/SamplePromotionSidebar';
import { SampleInteractionBottomNav } from '../../../components/SampleInteractionBottomNav';
import {
  getSampleSectionHides,
  getSectionDescriptionByType,
  getTextFromIdeaVocabFlow,
  IeltsSampleHideableSectionEnum,
  PreviewMain,
} from '../../../sample.utils';
import {
  writingTask2AcademicTableOfContent,
  WritingTask2AcademicTocId,
} from '../../CreateWritingTaskSample.step';
import { TitleH2Block } from '../SamplePreview';

const ExerciseEditorPreview = dynamic<any>(() =>
  import('../SampleExercisesPreview').then((mod) => mod.ExerciseEditorPreview)
);

const ConclusionPreview = dynamic<any>(() =>
  import('../../../components/Preview/ConclusionPreview').then((mod) => mod.ConclusionPreview)
);

const FeedbackPreview = dynamic<any>(() =>
  import('../../../components/Preview/FeedbackPreview').then((mod) => mod.FeedbackPreview)
);

const FlowVocabIdeaPreview = dynamic<any>(() =>
  import('../../../components/Preview/FlowVocabIdeaPreview').then((mod) => mod.FlowVocabIdeaPreview)
);

const OutlinePreview = dynamic<any>(() =>
  import('../../../components/Preview/OutlinePreview').then((mod) => mod.OutlinePreview)
);

const QuestionTitlePreview = dynamic<any>(() =>
  import('../../../components/Preview/QuestionTitlePreview').then((mod) => mod.QuestionTitlePreview)
);

const SectionDescriptionPreview = dynamic<any>(() =>
  import('../../../components/Preview/SectionDescriptionPreview').then(
    (mod) => mod.SectionDescriptionPreview
  )
);

const Main = styled(PreviewMain)`
  &.app-onlineTest {
    padding-bottom: 104px;
  }
`;

const NoHydrateSectionDescription = withHydrationOnDemand({
  on: ['idle'],
})(SectionDescriptionPreview);

const NoHydrateQuestionTitle = withHydrationOnDemand({
  on: ['idle'],
})(QuestionTitlePreview);

const NoHydrateFlowVocabIdea = withHydrationOnDemand({
  on: ['idle'],
})(FlowVocabIdeaPreview);

const WritingTask2PreviewBase = (props: any) => {
  const { seoData = { id: 'unknown' }, detailData, staticPageLink } = props;

  const sectionHides = getSampleSectionHides(detailData);
  const doExerciseContext = useDoExerciseProgressContext();

  const { isEmptyOutlineSection, isEmptyFlowIdeaSection } = React.useMemo(() => {
    const outlineSectionDescription = getSectionDescriptionByType(
      detailData,
      SectionDescriptionSectionTypeEnum.OUTLINE,
      true
    );
    const isEmptyOutline =
      isNodeStringEmpty(detailData?.outline?.title?.content) &&
      detailData?.outline?.contents.every(isNodeStringEmpty);
    const isEmptyOutlineDescription = defaultIsEmptyText(outlineSectionDescription?.content?.[0]);
    const flowIdeaSectionDescription = getSectionDescriptionByType(
      detailData,
      SectionDescriptionSectionTypeEnum.SAMPLES,
      true
    );
    const flowIdeaText = getTextFromIdeaVocabFlow(detailData);
    const isEmptyFlowIdea = flowIdeaText.trim().length === 0;
    const isEmptyFlowIdeaDescription = defaultIsEmptyText(flowIdeaSectionDescription?.content?.[0]);
    return {
      isEmptyOutlineSection: isEmptyOutlineDescription && isEmptyOutline,
      isEmptyFlowIdeaSection: isEmptyFlowIdeaDescription && isEmptyFlowIdea,
    };
  }, [detailData]);

  React.useEffect(() => {
    if (seoData && !doExerciseContext.pageId) {
      doExerciseContext.setPageId(seoData.id);
    }
  }, [doExerciseContext, seoData]);

  return (
    <Main className={`app-${getCurrentAppName()}`}>
      <SamplePromotionSidebar />
      <div className={cl('section-container')}>
        <div className="section-block">
          <IeltsSampleHeader
            routes={[
              {
                path: '/',
                breadcrumbName: 'Trang chủ',
              },
              {
                path: staticPageLink?.LANDING_IELTS_KNOWLEDGE?.url || '/',
                breadcrumbName: 'Kiến thức IELTS',
              },
              {
                path: staticPageLink?.LANDING_WRITING_SAMPLE?.url || '/',
                breadcrumbName: 'IELTS  Writing Sample',
              },
              {
                path: staticPageLink?.LANDING_WRITING_SAMPLE_T2A?.url || '/',
                breadcrumbName: getPageSampleTitleEnumTextByKey(detailData.type),
              },
            ]}
            title={detailData.title}
            description={detailData.description}
            lastModifiedAt={detailData?.lastModifiedAt}
            length={detailData?.length}
            seoData={seoData}
          />
        </div>
        {detailData.isTocShown && (
          <LeftSidebarPreviewSample
            items={writingTask2AcademicTableOfContent.filter((item) => {
              if (
                sectionHides[IeltsSampleHideableSectionEnum.EXERCISE] &&
                item.id === WritingTask2AcademicTocId.EXERCISES
              ) {
                return false;
              }
              if (
                sectionHides[IeltsSampleHideableSectionEnum.FEEDBACK] &&
                item.id === WritingTask2AcademicTocId.FEEDBACK
              ) {
                return false;
              }
              if (
                sectionHides[IeltsSampleHideableSectionEnum.CONCLUSION] &&
                item.id === WritingTask2AcademicTocId.LOI_KET
              ) {
                return false;
              }
              if (isEmptyOutlineSection && item.id === WritingTask2AcademicTocId.DAN_Y) {
                return false;
              }

              if (isEmptyFlowIdeaSection && item.id === WritingTask2AcademicTocId.BAI_MAU) {
                return false;
              }
              return true;
            })}
            tocTitle={detailData.title}
          />
        )}
        <div id={WritingTask2AcademicTocId.DE_BAI} className="section-block">
          <TitleH2Block>
            {getTocTitleById(WritingTask2AcademicTocId.DE_BAI, writingTask2AcademicTableOfContent)}
          </TitleH2Block>

          <div className="left-sidebar-stick right-sidebar-stick">
            <NoHydrateQuestionTitle value={detailData.question} />
          </div>
        </div>

        {!isEmptyOutlineSection && (
          <>
            <Separator variant="three-dot-horizontal" />
            <div className="grid-row">
              <div id={WritingTask2AcademicTocId.DAN_Y} className="section-block">
                <TitleH2Block>
                  {getTocTitleById(
                    WritingTask2AcademicTocId.DAN_Y,
                    writingTask2AcademicTableOfContent
                  )}
                </TitleH2Block>
                <SectionDescriptionPreview
                  detailData={detailData}
                  sectionType={SectionDescriptionSectionTypeEnum.OUTLINE}
                />
              </div>
              <div className="section-block mobile-full">
                <OutlinePreview detailData={detailData} />
              </div>
            </div>
          </>
        )}

        {!isEmptyFlowIdeaSection && (
          <>
            <Separator variant="three-dot-horizontal" />
            <div id={WritingTask2AcademicTocId.BAI_MAU} className="section-block">
              <NoHydrateFlowVocabIdea
                title={getTocTitleById(
                  WritingTask2AcademicTocId.BAI_MAU,
                  writingTask2AcademicTableOfContent
                )}
                renderDescription={() => (
                  <NoHydrateSectionDescription
                    detailData={detailData}
                    sectionType={SectionDescriptionSectionTypeEnum.SAMPLES}
                  />
                )}
                withTitleNode={false}
                detailData={detailData}
              />
            </div>
          </>
        )}
        <Separator variant="three-dot-horizontal" />
        <div id={WritingTask2AcademicTocId.VOCABULARY} className="section-block">
          <TitleH2Block>
            {getTocTitleById(
              WritingTask2AcademicTocId.VOCABULARY,
              writingTask2AcademicTableOfContent
            )}
          </TitleH2Block>
          <NoHydrateSectionDescription
            detailData={detailData}
            sectionType={SectionDescriptionSectionTypeEnum.VOCABS}
          />
          <VocabList vocabList={detailData?.vocab?.vocabs || []} />
        </div>
        {!sectionHides[IeltsSampleHideableSectionEnum.EXERCISE] && (
          <>
            <Separator variant="three-dot-horizontal" />
            <div id={WritingTask2AcademicTocId.EXERCISES} className="section-block">
              <TitleH2Block>
                {getTocTitleById(
                  WritingTask2AcademicTocId.EXERCISES,
                  writingTask2AcademicTableOfContent
                )}
              </TitleH2Block>
              <ExerciseEditorPreview detailData={detailData} />
            </div>
          </>
        )}
        {!sectionHides[IeltsSampleHideableSectionEnum.FEEDBACK] && (
          <>
            <Separator variant="three-dot-horizontal" />
            <div className="grid-row" id={WritingTask2AcademicTocId.FEEDBACK}>
              <div className="section-block">
                <TitleH2Block>
                  {getTocTitleById(
                    WritingTask2AcademicTocId.FEEDBACK,
                    writingTask2AcademicTableOfContent
                  )}
                </TitleH2Block>
                <NoHydrateSectionDescription
                  detailData={detailData}
                  sectionType={SectionDescriptionSectionTypeEnum.FEEDBACKS}
                />
              </div>
              <div className="section-block mobile-full">
                <FeedbackPreview detailData={detailData} />
              </div>
            </div>
          </>
        )}
        {!sectionHides[IeltsSampleHideableSectionEnum.CONCLUSION] && (
          <>
            <Separator variant="three-dot-horizontal" />
            <div id={WritingTask2AcademicTocId.LOI_KET} className="section-block">
              <TitleH2Block>
                {getTocTitleById(
                  WritingTask2AcademicTocId.LOI_KET,
                  writingTask2AcademicTableOfContent
                )}
              </TitleH2Block>
              <ConclusionPreview detailData={detailData} />
            </div>
          </>
        )}
        <Separator variant="three-dot-horizontal" />
        <div id="footer-author" className="section-block">
          <AuthorPreview
            seoData={seoData}
            lastModifiedAt={detailData.lastModifiedAt}
            length={detailData.length}
          />
        </div>
      </div>

      <SampleInteractionBottomNav
        seoData={seoData}
        tocItems={writingTask2AcademicTableOfContent}
        tocTitle={detailData.title}
      />
    </Main>
  );
};

export const WritingTask2PreviewUI = compose(
  withStoreRequiredForEditor({
    haveDoTestExercise: true,
    initFileUploadImmediately: true,
  }),
  observer
)(WritingTask2PreviewBase);
